"use client";

import { Check, Loader2 } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  type BookingContactDetails,
  saveBookingContactDetails,
} from "@/actions/booking-contact-details";
import { Button } from "@/components/ui/button";
import { formatPhoneForDisplay } from "@/lib/phone-number";
import { cn } from "@/lib/utils";

/**
 * Name and email for a booking, captured without an OTP.
 *
 * Shared by the three places that ask: the payment screen, the ticket in the
 * wallet, and the live card on the day of the event. One component because the
 * ask has to look and behave identically in all three — somebody who skipped it
 * at checkout should recognise the same two fields at the door, already holding
 * whatever they did give us.
 *
 * The wallet can keep the lightweight save-on-blur behavior. Checkout and the
 * day-of card opt into one explicit Save changes action so it is unambiguous
 * whether both values reached the organiser before the member moves on.
 */

type FieldKey = "name" | "email";
type FieldState = "idle" | "saving" | "saved" | "error";

export function BookingContactFields({
  initial,
  context,
  onSaved,
  onEmailVerificationRequired,
  showPhone = true,
  saveMode = "blur",
  className,
  /** Rendered under the fields — the invoice note, usually. */
  footnote,
}: {
  initial: BookingContactDetails;
  context: "event_checkout" | "wallet_ticket" | "live_event_card";
  onSaved?: (details: BookingContactDetails) => void;
  /** Opens the existing OTP flow when a proven address is being replaced. */
  onEmailVerificationRequired?: (email: string) => void;
  showPhone?: boolean;
  saveMode?: "blur" | "explicit";
  className?: string;
  footnote?: React.ReactNode;
}) {
  const nameId = useId();
  const emailId = useId();

  // The placeholder is never seeded into the box. The whole point of the field
  // is to replace "Hiive member", and prefilling it invites somebody to accept
  // it as though it were their name.
  const [name, setName] = useState(initial.name ?? "");
  const [email, setEmail] = useState(initial.email ?? "");
  const [state, setState] = useState<Record<FieldKey, FieldState>>({
    name: "idle",
    email: "idle",
  });
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});

  // What is actually on the server, so a blur that changed nothing does not
  // fire a write — and so a save that failed can be retried by blurring again.
  const savedRef = useRef({
    name: initial.name ?? "",
    email: initial.email ?? "",
  });
  // Server actions for the two independent fields can finish out of order.
  // Keep the newest client-known value per field instead of letting a slower
  // response replace the other field with the snapshot it happened to read.
  const detailsRef = useRef(initial);
  const savedTimersRef = useRef<Partial<Record<FieldKey, number>>>({});

  useEffect(() => {
    const nextName = initial.name ?? "";
    const nextEmail = initial.email ?? "";
    const previousName = savedRef.current.name;
    const previousEmail = savedRef.current.email;

    detailsRef.current = initial;

    // An OTP drawer can update the same contact record outside this component.
    // Adopt that server result without trampling text the member is currently
    // editing in the other field.
    if (nextName !== previousName) {
      savedRef.current.name = nextName;
      setName((current) =>
        current.trim() === previousName || current.trim() === nextName
          ? nextName
          : current,
      );
      setErrors((current) => ({ ...current, name: undefined }));
      setState((current) => ({ ...current, name: "idle" }));
    }
    if (nextEmail !== previousEmail) {
      savedRef.current.email = nextEmail;
      setEmail((current) =>
        current.trim() === previousEmail || current.trim() === nextEmail
          ? nextEmail
          : current,
      );
      setErrors((current) => ({ ...current, email: undefined }));
      setState((current) => ({ ...current, email: "idle" }));
    }
  }, [initial]);

  useEffect(() => {
    return () => {
      for (const timer of Object.values(savedTimersRef.current)) {
        if (timer) window.clearTimeout(timer);
      }
    };
  }, []);

  const flashSaved = useCallback((field: FieldKey) => {
    setState((current) => ({ ...current, [field]: "saved" }));
    const existing = savedTimersRef.current[field];
    if (existing) window.clearTimeout(existing);
    savedTimersRef.current[field] = window.setTimeout(() => {
      setState((current) =>
        current[field] === "saved" ? { ...current, [field]: "idle" } : current,
      );
    }, 2400);
  }, []);

  const commit = useCallback(
    async (field: FieldKey, rawValue: string) => {
      const value = rawValue.trim();
      if (!value || value === savedRef.current[field]) return;

      setState((current) => ({ ...current, [field]: "saving" }));
      setErrors((current) => ({ ...current, [field]: undefined }));

      try {
        const result = await saveBookingContactDetails(
          field === "name"
            ? { name: value, context }
            : { email: value, context },
        );
        if (!result.ok) {
          setState((current) => ({ ...current, [field]: "error" }));
          setErrors((current) => ({ ...current, [field]: result.message }));
          if (field === "email" && result.code === "VERIFIED_EMAIL_EXISTS") {
            onEmailVerificationRequired?.(value);
          }
          return;
        }

        const savedValue = result.data[field] ?? "";
        savedRef.current = { ...savedRef.current, [field]: savedValue };
        const nextDetails: BookingContactDetails = {
          ...detailsRef.current,
          [field]: result.data[field],
          ...(field === "email"
            ? { emailVerified: result.data.emailVerified }
            : {}),
          phone: result.data.phone ?? detailsRef.current.phone,
          invoiceReady: Boolean(
            field === "name"
              ? result.data.name && detailsRef.current.email
              : detailsRef.current.name && result.data.email,
          ),
        };
        detailsRef.current = nextDetails;
        flashSaved(field);
        onSaved?.(nextDetails);
      } catch {
        setState((current) => ({ ...current, [field]: "error" }));
        setErrors((current) => ({
          ...current,
          [field]: "We couldn't save that. Try again.",
        }));
      }
    },
    [context, flashSaved, onEmailVerificationRequired, onSaved],
  );

  const commitAll = useCallback(async () => {
    const nextName = name.trim();
    const nextEmail = email.trim();
    const nameChanged = nextName !== savedRef.current.name;
    const emailChanged = nextEmail !== savedRef.current.email;
    if (!nameChanged && !emailChanged) return;

    const nextErrors: Partial<Record<FieldKey, string>> = {};
    if (nameChanged && !nextName) {
      nextErrors.name = "Enter your full name or restore the saved one.";
    }
    if (emailChanged && !nextEmail) {
      nextErrors.email = "Enter your email or restore the saved one.";
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setState((current) => ({
        ...current,
        ...(nextErrors.name ? { name: "error" as const } : {}),
        ...(nextErrors.email ? { email: "error" as const } : {}),
      }));
      return;
    }

    const changedFields: FieldKey[] = [
      ...(nameChanged ? (["name"] as const) : []),
      ...(emailChanged ? (["email"] as const) : []),
    ];
    setErrors({});
    setState((current) => ({
      ...current,
      ...Object.fromEntries(changedFields.map((field) => [field, "saving"])),
    }));

    try {
      const result = await saveBookingContactDetails({
        ...(nameChanged ? { name: nextName } : {}),
        ...(emailChanged ? { email: nextEmail } : {}),
        context,
      });
      if (!result.ok) {
        const errorField = result.code?.includes("EMAIL") ? "email" : "name";
        setState((current) => ({
          ...current,
          ...Object.fromEntries(changedFields.map((field) => [field, "idle"])),
          [errorField]: "error",
        }));
        setErrors({ [errorField]: result.message });
        if (errorField === "email" && result.code === "VERIFIED_EMAIL_EXISTS") {
          onEmailVerificationRequired?.(nextEmail);
        }
        return;
      }

      savedRef.current = {
        name: result.data.name ?? "",
        email: result.data.email ?? "",
      };
      detailsRef.current = result.data;
      setName(result.data.name ?? "");
      setEmail(result.data.email ?? "");
      for (const field of changedFields) flashSaved(field);
      onSaved?.(result.data);
    } catch {
      setState((current) => ({
        ...current,
        ...Object.fromEntries(changedFields.map((field) => [field, "error"])),
      }));
      setErrors(
        Object.fromEntries(
          changedFields.map((field) => [
            field,
            "We couldn't save that. Try again.",
          ]),
        ),
      );
    }
  }, [context, email, flashSaved, name, onEmailVerificationRequired, onSaved]);

  const hasChanges =
    name.trim() !== savedRef.current.name ||
    email.trim() !== savedRef.current.email;
  const isSaving = Object.values(state).includes("saving");
  const justSaved =
    !hasChanges && Object.values(state).some((value) => value === "saved");

  const inputClassName =
    "mt-2 h-11 w-full rounded-xl border border-border bg-surface-alt px-3 font-medium text-base text-text-primary outline-none placeholder:text-text-tertiary focus:border-brand disabled:opacity-60 md:text-sm dark:border-white/[0.12] dark:bg-glass-bg-06 dark:text-white dark:focus:border-white/35 dark:placeholder:text-white/38";

  const statusFor = (field: FieldKey) => {
    if (state[field] === "saving") {
      return (
        <Loader2
          className="size-3.5 animate-spin text-text-tertiary"
          aria-hidden="true"
        />
      );
    }
    if (state[field] === "saved") {
      return <Check className="size-3.5 text-brand" aria-hidden="true" />;
    }
    return null;
  };

  return (
    <div className={cn("space-y-4", className)}>
      {showPhone && initial.phone ? (
        <div>
          <span className="text-sm font-medium text-text-secondary dark:text-white/64">
            Mobile
          </span>
          <div className="mt-2 flex h-11 w-full items-center rounded-xl border border-dashed border-border bg-surface-alt px-3 text-base font-medium text-text-secondary md:text-sm dark:border-white/[0.12] dark:bg-glass-bg-04 dark:text-white/64">
            {formatPhoneForDisplay(initial.phone)}
          </div>
        </div>
      ) : null}

      <div>
        <label
          htmlFor={nameId}
          className="flex items-center gap-2 text-sm font-medium text-text-secondary dark:text-white/64"
        >
          Full name
          {statusFor("name")}
        </label>
        <input
          id={nameId}
          type="text"
          autoComplete="name"
          autoCapitalize="words"
          maxLength={100}
          value={name}
          disabled={saveMode === "explicit" && isSaving}
          onChange={(event) => {
            setErrors((current) => ({ ...current, name: undefined }));
            setState((current) => ({ ...current, name: "idle" }));
            setName(event.target.value);
          }}
          onBlur={
            saveMode === "blur"
              ? (event) => void commit("name", event.target.value)
              : undefined
          }
          placeholder="Your name"
          aria-invalid={Boolean(errors.name)}
          className={inputClassName}
        />
        {errors.name ? (
          <p className="mt-2 text-sm text-destructive" role="alert">
            {errors.name}
          </p>
        ) : null}
      </div>

      <div>
        <label
          htmlFor={emailId}
          className="flex items-center gap-2 text-sm font-medium text-text-secondary dark:text-white/64"
        >
          Email
          {statusFor("email")}
        </label>
        <input
          id={emailId}
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          value={email}
          disabled={saveMode === "explicit" && isSaving}
          onChange={(event) => {
            setErrors((current) => ({ ...current, email: undefined }));
            setState((current) => ({ ...current, email: "idle" }));
            setEmail(event.target.value);
          }}
          onBlur={
            saveMode === "blur"
              ? (event) => void commit("email", event.target.value)
              : undefined
          }
          placeholder="you@example.com"
          aria-invalid={Boolean(errors.email)}
          className={inputClassName}
        />
        {errors.email ? (
          <p className="mt-2 text-sm text-destructive" role="alert">
            {errors.email}
          </p>
        ) : null}
      </div>

      {footnote ? (
        <p className="text-sm text-text-tertiary dark:text-white/50">
          {footnote}
        </p>
      ) : null}

      {saveMode === "explicit" ? (
        <Button
          type="button"
          onClick={() => void commitAll()}
          disabled={!hasChanges || isSaving}
          className="h-11 w-full rounded-xl bg-brand font-semibold text-brand-foreground hover:bg-brand/90 dark:bg-brand dark:text-brand-foreground dark:hover:bg-brand/90"
        >
          {isSaving ? (
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          ) : justSaved ? (
            <Check className="size-4" aria-hidden="true" />
          ) : null}
          <span aria-live="polite">
            {isSaving
              ? "Saving…"
              : justSaved
                ? "Changes saved"
                : "Save changes"}
          </span>
        </Button>
      ) : null}
    </div>
  );
}
